/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Dashboard Operations
 * 
 * This module handles dashboard-related API operations including:
 * - Activity feed data
 * - Velocity metrics and trends
 */

import { buildProductApiUrl } from './base';
import type {
  ActivityResult,
  VelocityDataResult,
} from '../../../server/types';

// ============================================================================
// Dashboard Operations
// ============================================================================

/**
 * Get activity data for the dashboard
 * 
 * @param days - Number of days to fetch activity for (default 7)
 * @returns Activity items
 */
export async function getDashboardActivity(days: number = 7): Promise<ActivityResult> {
  try {
    const apiUrl = buildProductApiUrl('/api/dashboard/activity', { days });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      return {
        activities: [],
        error: `Failed to fetch activity (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error fetching dashboard activity:', error);
    return {
      activities: [],
      error: error.message || 'Failed to fetch dashboard activity'
    };
  }
}

/**
 * Get velocity data for the dashboard
 * 
 * @param weeks - Number of weeks to fetch velocity for (default 8)
 * @returns Velocity data by product and week
 */
export async function getDashboardVelocity(weeks: number = 8): Promise<VelocityDataResult> {
  try {
    const apiUrl = buildProductApiUrl('/api/dashboard/velocity', { weeks });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      return {
        weeks: [],
        products: [],
        error: `Failed to fetch velocity (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error fetching dashboard velocity:', error);
    return {
      weeks: [],
      products: [],
      error: error.message || 'Failed to fetch dashboard velocity'
    };
  }
}

